import { useEffect, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { MONITORING_MOCK } from "./data";
import { useSavePlantSettings } from "./hooks";
import { SYSTEM_KWP, SYSTEM_COST_RS, INSTALL_DATE, fmtDate, fmtRs } from "./mockData";

// Settings tab - customer-editable tariff (drives all ₹ savings numbers)
// plus "Your system" plant metadata.
// Ported from solar-dashboard/src/components/SettingsPanel.tsx. Differences:
// onTariffChange may return a promise (live mode PUTs to the backend); the
// success toast only fires once it resolves. System info rows come from the
// `systemInfo` prop (live plant metadata) and are inline-editable in live
// mode (pencil → input → save → PUT /monitoring/settings/plant → invalidate
// ["monitoring"]); mock mode keeps the panel read-only with demo values.

const INK = "#0d201d";
const MUTED = "#5a6e6a";
const FAINT = "#71807d";
const DEEP_GOLD = "#8a6826";
const PALE_GOLD = "#f4ead4";
const TEAL = "#237a6e";
const BORDER = "#e8e4d8";
const DISPLAY = "'Bricolage Grotesque', Inter, sans-serif";

// Editable "Your system" rows. `toPayload` keys match the backend contract
// (PUT /api/monitoring/settings/plant); unset values display "-" and stay
// editable. Mock mode never edits - `mockValue` reproduces the original
// read-only demo rows exactly.
const SYSTEM_FIELDS = [
  {
    key: "capacity",
    label: "Capacity",
    testId: "settings-edit-capacity",
    inputType: "number",
    inputProps: { inputMode: "decimal", step: "0.1", min: "0" },
    aria: "Capacity in kW",
    value: (info) => info.systemKwp ?? null,
    mockValue: (info) => info.systemKwp ?? SYSTEM_KWP,
    display: (v) => (v != null ? `${v} kW rooftop` : "-"),
    toDraft: (v) => (v != null ? String(v) : ""),
    isValid: (d) => {
      const n = parseFloat(d);
      return !isNaN(n) && n > 0;
    },
    toPayload: (d) => ({ system_kwp: parseFloat(d) }),
  },
  {
    key: "installed",
    label: "Installed",
    testId: "settings-edit-installed",
    inputType: "date",
    inputProps: {},
    aria: "Installation date",
    value: (info) => info.installDate ?? null,
    mockValue: (info) => info.installDate ?? INSTALL_DATE.toISOString(),
    display: (v) => (v ? fmtDate(new Date(v)) : "-"),
    toDraft: (v) => (v ? String(v).slice(0, 10) : ""),
    isValid: (d) => d !== "" && !isNaN(new Date(d).getTime()),
    toPayload: (d) => ({ install_date: d }),
  },
  {
    key: "location",
    label: "Location",
    testId: "settings-edit-location",
    inputType: "text",
    inputProps: { placeholder: "City, State" },
    aria: "Plant location",
    value: (info) => info.location || null,
    mockValue: (info) => info.location ?? "Rajkot, Gujarat",
    display: (v) => v ?? "-",
    toDraft: (v) => v ?? "",
    isValid: (d) => d.trim() !== "",
    toPayload: (d) => ({ location: d.trim() }),
  },
  {
    key: "inverter",
    label: "Inverter",
    testId: "settings-edit-inverter",
    inputType: "text",
    inputProps: { placeholder: "Make & model" },
    aria: "Inverter model",
    value: (info) => info.inverter || null,
    mockValue: (info) => info.inverter ?? "Growatt MIN 5000TL-X",
    display: (v) => v ?? "-",
    toDraft: (v) => v ?? "",
    isValid: (d) => d.trim() !== "",
    toPayload: (d) => ({ inverter_model: d.trim() }),
  },
  {
    key: "cost",
    label: "System cost",
    testId: "settings-edit-cost",
    inputType: "number",
    inputProps: { inputMode: "numeric", step: "1000", min: "0" },
    aria: "System cost in rupees",
    value: (info) => info.systemCostRs ?? null,
    mockValue: (info) => info.systemCostRs ?? SYSTEM_COST_RS,
    display: (v) => (v != null ? fmtRs(v) : "-"),
    toDraft: (v) => (v != null ? String(v) : ""),
    isValid: (d) => {
      const n = parseFloat(d);
      return !isNaN(n) && n >= 0; // 0 = not tracked (greys out payback)
    },
    toPayload: (d) => ({ system_cost_rs: Math.round(parseFloat(d)) }),
  },
];

// One "Your system" row: value + pencil at rest, input + save/cancel while
// editing - the same interaction pattern as the tariff editor above.
function SystemRow({ field, value, editable, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editable) {
      setEditing(false);
      setDraft("");
    }
  }, [editable]);

  const valid = field.isValid(draft);

  function startEdit() {
    setDraft(field.toDraft(value));
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setDraft("");
  }

  async function save() {
    if (!editable || !valid || busy) return;
    setBusy(true);
    try {
      await onSave(field.toPayload(draft));
      toast.success(`${field.label} updated`);
      setEditing(false);
      setDraft("");
    } catch {
      toast.error(`Couldn't save the ${field.label.toLowerCase()} - try again`);
    } finally {
      setBusy(false);
    }
  }

  if (!editing || !editable) {
    return (
      <div className="flex items-center justify-between">
        <span className="text-[14px]" style={{ color: MUTED }}>
          {field.label}
        </span>
        <span className="flex items-center gap-1">
          <span
            className="text-[14px] font-medium"
            style={{ color: value != null ? INK : FAINT }}
          >
            {field.display(value)}
          </span>
          {editable && (
            <button
              data-testid={field.testId}
              onClick={startEdit}
              className="flex h-8 w-8 items-center justify-center rounded-full active:scale-[0.98] transition-transform duration-150"
              style={{ color: FAINT }}
              aria-label={`Edit ${field.label.toLowerCase()}`}
            >
              <Pencil size={13} />
            </button>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[14px]" style={{ color: MUTED }}>
        {field.label}
      </span>
      <span className="flex items-center gap-1.5">
        <input
          data-testid={`${field.testId}-input`}
          type={field.inputType}
          {...field.inputProps}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-36 rounded-[10px] border px-2.5 py-1.5 text-[14px] font-medium text-right outline-none focus:ring-2"
          style={{
            borderColor: valid || draft === "" ? BORDER : "#b3241a",
            color: INK,
            ["--tw-ring-color"]: PALE_GOLD,
          }}
          aria-label={field.aria}
        />
        <button
          data-testid={`${field.testId}-save`}
          onClick={save}
          disabled={!valid || busy}
          className="flex h-8 w-8 items-center justify-center rounded-full text-white active:scale-[0.98] transition-transform duration-150 disabled:opacity-30"
          style={{ background: INK }}
          aria-label={`Save ${field.label.toLowerCase()}`}
        >
          <Check size={14} />
        </button>
        <button
          data-testid={`${field.testId}-cancel`}
          onClick={cancel}
          disabled={busy}
          className="flex h-8 w-8 items-center justify-center rounded-full active:scale-[0.98] transition-transform duration-150 disabled:opacity-30"
          style={{ color: MUTED }}
          aria-label={`Cancel editing ${field.label.toLowerCase()}`}
        >
          <X size={14} />
        </button>
      </span>
    </div>
  );
}

export default function SettingsPanel({ tariff, onTariffChange, systemInfo, readOnly = false }) {
  const [draft, setDraft] = useState(tariff.toString());
  const [savedFlash, setSavedFlash] = useState(false);

  // Re-sync the input when the tariff changes externally (live mode: the
  // server-persisted value lands asynchronously via the summary query).
  useEffect(() => setDraft(tariff.toString()), [tariff]);

  const parsed = parseFloat(draft);
  const valid = !isNaN(parsed) && parsed > 0 && parsed < 100;
  const dirty = valid && parsed !== tariff;

  const info = systemInfo ?? {};
  const savePlantSettings = useSavePlantSettings(info.plantId);
  // Live mode shows the server's real plant values ("-" when unset, all
  // editable); mock mode keeps the original read-only demo rows.
  const systemValues = SYSTEM_FIELDS.map((f) =>
    MONITORING_MOCK ? f.mockValue(info) : f.value(info)
  );

  async function save() {
    if (!dirty) return;
    try {
      await onTariffChange(parsed);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      toast.success("Tariff saved");
    } catch {
      toast.error("Couldn't save the tariff - try again");
    }
  }

  return (
    <div className="space-y-4">
      {/* Tariff */}
      <div
        className="bg-white rounded-[18px] shadow-[0_2px_14px_rgba(13,32,29,0.05)] border p-5"
        style={{ borderColor: BORDER }}
      >
        <p className="text-[12px] font-medium tracking-wide" style={{ color: MUTED }}>
          Your electricity tariff
        </p>
        <p className="mt-1 text-[13px] leading-snug" style={{ color: FAINT }}>
          We use this to convert every kWh your plant generates into rupees saved. Check your latest
          electricity bill for your per-unit rate.
        </p>
        {readOnly && (
          <p className="mt-2 text-[12px]" style={{ color: MUTED }}>
            Only the plant owner can change these settings
          </p>
        )}
        <div className="mt-4 flex items-center gap-3">
          <div
            className="flex flex-1 items-center rounded-[14px] border px-4 py-3 focus-within:ring-2"
            style={{
              borderColor: dirty ? DEEP_GOLD : BORDER,
              ["--tw-ring-color"]: PALE_GOLD,
            }}
          >
            <span className="text-[18px] font-medium mr-1" style={{ color: MUTED }}>
              ₹
            </span>
            <input
              data-testid="tariff-input"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={draft}
              disabled={readOnly}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full bg-transparent text-[20px] font-medium tracking-tight outline-none"
              style={{ fontFamily: DISPLAY, color: INK }}
              aria-label="Tariff in rupees per kWh"
            />
            <span className="text-[13px] whitespace-nowrap" style={{ color: FAINT }}>
              / kWh
            </span>
          </div>
          {!readOnly && <button
            data-testid="tariff-save-button"
            onClick={save}
            disabled={!dirty}
            className="rounded-full px-5 py-3 text-[14px] font-semibold text-white active:scale-[0.98] transition-transform duration-150 disabled:opacity-30"
            style={{ background: savedFlash ? TEAL : INK }}
          >
            {savedFlash ? "Saved ✓" : "Save"}
          </button>}
        </div>
        {!valid && draft !== "" && (
          <p className="mt-2 text-[12px]" style={{ color: "#b3241a" }}>
            Enter a valid rate between 0 and 100.
          </p>
        )}
        <p className="mt-3 text-[12px]" style={{ color: FAINT }}>
          Currently applied: {fmtRs(tariff)} per kWh · changes apply to all views and reports
          instantly.
        </p>
      </div>

      {/* System info (live values, inline-editable; read-only in mock mode) */}
      <div
        className="bg-white rounded-[18px] shadow-[0_2px_14px_rgba(13,32,29,0.05)] border p-5"
        style={{ borderColor: BORDER }}
      >
        <p className="text-[12px] font-medium tracking-wide mb-3" style={{ color: MUTED }}>
          Your system
        </p>
        <div className="space-y-3">
          {SYSTEM_FIELDS.map((field, i) => (
            <SystemRow
              key={field.key}
              field={field}
              value={systemValues[i]}
              editable={!MONITORING_MOCK && !readOnly}
              onSave={savePlantSettings.mutateAsync}
            />
          ))}
          <div className="flex items-center justify-between">
            <span className="text-[14px]" style={{ color: MUTED }}>
              Monitoring
            </span>
            <span className="text-[14px] font-medium" style={{ color: INK }}>
              {info.monitoring ?? "ShineMonitor · live"}
            </span>
          </div>
        </div>
      </div>

      {/* Support */}
      <div
        className="bg-white rounded-[18px] shadow-[0_2px_14px_rgba(13,32,29,0.05)] border p-5"
        style={{ borderColor: BORDER }}
      >
        <p className="text-[12px] font-medium tracking-wide mb-3" style={{ color: MUTED }}>
          Support
        </p>
        <div className="space-y-3">
          {[
            ["Service helpline", "1800-XXX-XXXX"],
            ["WhatsApp support", "+91 98XXX XXXXX"],
            ["App version", "1.0.0"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-[14px]" style={{ color: MUTED }}>
                {k}
              </span>
              <span className="text-[14px] font-medium" style={{ color: TEAL }}>
                {v}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="px-1 pt-1 text-center text-[12px]" style={{ color: FAINT }}>
        Greentek India Limited · alliance.greentekindia.co.in
      </p>
    </div>
  );
}
